/**
 * SFRA -> Segment event-replay fetch interceptor (SFCC-11725 / LC-AN-04).
 *
 * Ports hec-composable `overrides/app/utils/segment/segmentDataLayerInterceptor.js`.
 *
 * SFRA controllers (e.g. EmailSubscribe-SubscribeEmail, the email popup target,
 * and the SFRA-proxied hybrid checkout/account pages) build their Segment events
 * server-side and return them in the JSON response body as a `segmentDataLayer`
 * array, each entry flagged `isSegmentEventSent: 0`. On SFRA pages a client-side
 * script (`segmentInit.js`) picks them up; on the Workers storefront there was no
 * consumer, so during the hybrid window those events never reached the client.
 * This restores that bridge.
 *
 * Behaviour (matches the rest of the Segment suite):
 *  - CLIENT-ONLY: bails on the server (no `window`); installed exactly once.
 *  - Wraps `window.fetch`; inspects only SAME-ORIGIN JSON responses. Cross-origin,
 *    opaque, and non-JSON responses are passed straight through untouched.
 *  - Reads `response.clone()` so the original body stream is never consumed —
 *    RR7 loader fetches / prefetch and every other caller still get their body.
 *  - NEVER throws: any parse / consumed-body / transient error is swallowed so a
 *    bad body can never reject a fetch the rest of the app depends on.
 *  - `identify` entries replay BEFORE `track` so the userId is set on the
 *    analytics instance before tracks ship (mirrors SFRA `segmentInit.js`'s
 *    identify-first sort; otherwise consent tracks lose the userId and get
 *    dropped by the source-side consent middleware).
 *  - Dedup: entries already flagged `isSegmentEventSent` are skipped so a
 *    revalidation that re-fetches the same response doesn't double-emit.
 *
 * Consent / TPA gating: this only forwards events through `window.analytics`.
 * That instance is created by <Segment /> with the TrustArc destination-consent
 * decision already applied (see `app/segment/index.tsx`), and TPA is downstream
 * destination context — never a first-party suppression gate. So replayed events
 * inherit the exact same consent posture as every other Segment call, with no
 * extra gating here.
 *
 * RETIREMENT (COM-585, martech 2026-09-26): this bridge exists only for the
 * hybrid window. The events it replays are SERVER-authored by SFRA, so they
 * cannot carry the page-context join key (`hec_navigation_id`) — that gap is
 * accepted as out of scope and closes on its own as SFRA pages migrate to
 * Composable. Do NOT invest in stamping SFRA payloads. Retire this module as
 * part of the checkout migration, once no SFRA-proxied page returns a
 * `segmentDataLayer` array.
 *
 * SFCC-11936: the replay runs as a DETACHED task that awaits `waitForAnalytics()`
 * rather than bailing synchronously when `window.analytics` isn't assigned yet.
 * The Bloomreach email-popup weblayer POSTs to SFRA (EmailSubscribe-
 * SubscribeEmail) within moments of page load — often before <Segment /> has
 * loaded — and the old synchronous bail silently dropped the replayed
 * identify+consent while the weblayer's own pre-consent still landed. Detaching
 * keeps the hard rule intact: the response is NEVER delayed by the replay. When
 * analytics never loads (e.g. WDH, no write key) the 5s ready-wait lapses and
 * the replay is skipped as before.
 */
import { waitForAnalytics } from "./utils";

type SegmentDataLayerItem = {
  action?: "identify" | "track";
  event?: string;
  data?: Record<string, unknown>;
  isSegmentEventSent?: number | boolean;
};

/**
 * Replays one response's `segmentDataLayer` entries into Segment. Detached from
 * the fetch return path (fire-and-forget) so the `waitForAnalytics()` ready-wait
 * (5s cap) can never delay a response (SFCC-11936 — see file header).
 */
export async function replaySegmentDataLayer(
  items: SegmentDataLayerItem[],
): Promise<void> {
  await waitForAnalytics();
  const analytics = window.analytics;
  if (!analytics) return;

  // identify-first ordering (see file header).
  const ordered = [...items].sort((a, b) => {
    if (a?.action === "identify" && b?.action !== "identify") return -1;
    if (a?.action !== "identify" && b?.action === "identify") return 1;
    return 0;
  });

  for (const item of ordered) {
    if (!item || item.isSegmentEventSent) continue;
    try {
      if (item.action === "identify" && item.event) {
        void analytics.identify(item.event, item.data);
      } else if (item.action === "track" && item.event) {
        void analytics.track(item.event, item.data);
      }
    } catch {
      // Never let one bad entry abort the rest of the replay.
    }
  }
}

declare global {
  interface Window {
    __segmentDataLayerInterceptorInstalled?: boolean;
  }
}

function resolveRequestUrl(input: unknown): string | undefined {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (input instanceof Request) return input.url;
  return undefined;
}

/**
 * Installs the same-origin `segmentDataLayer` replay interceptor on `window.fetch`.
 * Safe to call repeatedly — only the first call patches `fetch`.
 */
export function installSegmentDataLayerInterceptor(): void {
  if (
    typeof window === "undefined" ||
    window.__segmentDataLayerInterceptorInstalled
  ) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  const sameOrigin = window.location.origin;

  window.fetch = async function patchedFetch(
    ...args: Parameters<typeof fetch>
  ): Promise<Response> {
    const response = await originalFetch(...args);
    try {
      const requestUrl = resolveRequestUrl(args[0]);
      const isSameOrigin =
        !!requestUrl &&
        (requestUrl.startsWith("/") || requestUrl.startsWith(sameOrigin));
      if (!isSameOrigin) return response;

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("json")) return response;

      // Clone so the caller's body stream is untouched (RR7 loaders/prefetch).
      const data: unknown = await response.clone().json();
      const segmentDataLayer = (data as { segmentDataLayer?: unknown })
        ?.segmentDataLayer;
      if (!Array.isArray(segmentDataLayer) || segmentDataLayer.length === 0) {
        return response;
      }

      // Detached replay: awaits Segment readiness without holding the response.
      void replaySegmentDataLayer(segmentDataLayer as SegmentDataLayerItem[]);
    } catch {
      // Body not JSON, already consumed, or another transient parse issue.
      // Never block the response on interceptor failure.
    }
    return response;
  };

  window.__segmentDataLayerInterceptorInstalled = true;
}
